import { EmptyIntroducers } from '../components/EmptyState';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import InviteModal from '../components/InviteModal';
import BulkInviteModal from '../components/BulkInviteModal';
import AgreementReviewPanel from '../components/AgreementReviewPanel';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { useMobile } from '../hooks/useMobile';

const STATUS_CONFIG = {
  pending:            { label: 'Invited',           bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  accepted:           { label: 'Signed Up',         bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  awaiting_agreement: { label: 'Awaiting Agreement', bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  agreement_pending:  { label: 'Awaiting Review',   bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  agreement_countersigned:   { label: 'Countersigned', bg: '#e0e7ff', color: '#3730a3', border: '#a5b4fc' },
  agreement_with_compliance: { label: 'With Network',  bg: '#e0e7ff', color: '#3730a3', border: '#a5b4fc' },
  agreement_approved: { label: 'Active',            bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
  agreement_rejected: { label: 'Rejected',          bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
  suspended:          { label: 'Suspended',        bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  expired:            { label: 'Expired',           bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' },
  archived:           { label: 'Archived',          bg: '#f3f4f6', color: '#9ca3af', border: '#e5e7eb' },
};

function getStatus(inv) {
  if (inv.status === 'archived') return 'archived';
  if (inv.status === 'expired') return 'expired';
  if (inv.user_status === 'suspended') return 'suspended';
  if (inv.agreement_status === 'approved') return 'agreement_approved';
  if (inv.agreement_status === 'rejected') return 'agreement_rejected';
  if (inv.agreement_status === 'pending') return 'agreement_pending';
  if (inv.agreement_status === 'countersigned') return 'agreement_countersigned';
  if (inv.agreement_status === 'with_compliance') return 'agreement_with_compliance';
  // Waived agreement: user is already active — show as Active
  if (inv.agreement_required === false && inv.user_status === 'active') return 'agreement_approved';
  // Signed up, an agreement is expected, but nothing has been submitted at all
  // yet (no introducer_agreements row -- agreement_status is null/undefined,
  // not the string 'pending'). Distinct from agreement_pending, which means a
  // submission exists and is awaiting review. Without this, these introducers
  // showed as plain "Signed Up" even though they're stuck waiting on an
  // agreement (Tim, 9 July — found via a real stuck introducer on GFS).
  if (inv.status === 'accepted' && inv.agreement_required !== false && !inv.agreement_status) {
    return 'awaiting_agreement';
  }
  if (inv.status === 'accepted') return 'accepted';
  return 'pending';
}

function StatusBadge({ inv }) {
  const key = getStatus(inv);
  const cfg = STATUS_CONFIG[key] || STATUS_CONFIG.pending;
  return (
    <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 12, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

export default function IntroducersPage() {
  const mobile = useMobile();
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [detailsInv, setDetailsInv] = useState(null); // invited-but-not-signed-up details modal
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isPlatformOwner = currentUser.role === 'platform_owner';

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/invites', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setInvites(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const [copiedToken, setCopiedToken] = useState(null);

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/signup/${token}`);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const resendInvite = async (email) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/invites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, resend: true })
      });
      if (res.ok) load();
    } catch (err) {
      console.error(err);
    }
  };

  const sendResetLink = async (inv) => {
    if (!window.confirm(`Send a password reset link to ${inv.email}?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/auth/send-reset-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: inv.introducer_user_id }),
      });
      const data = await res.json().catch(() => ({}));
      alert(res.ok ? (data.message || 'Reset link sent.') : (data.error || 'Failed to send reset link'));
    } catch (err) { console.error(err); }
  };

  const archiveIntroducer = async (id) => {
    if (!window.confirm('Archive this introducer? They will be hidden from active lists but retained for compliance.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/invites/${id}/archive`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) load();
      else {
        const data = await res.json();
        alert(data.error || 'Failed to archive');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const [sendingAgreementId, setSendingAgreementId] = useState(null);

  const sendAgreementForSigning = async (inv) => {
    if (!window.confirm(`Send an agreement signing link to ${inv.introducer_name || inv.email}?`)) return;
    setSendingAgreementId(inv.introducer_user_id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/introducers/${inv.introducer_user_id}/send-agreement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        alert('Agreement signing link sent.');
        load();
      } else {
        alert(data.error || 'Failed to send agreement');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to send agreement');
    } finally {
      setSendingAgreementId(null);
    }
  };

  const activateIntroducer = async (inv) => {
    if (!window.confirm(`Manually activate ${inv.introducer_name || inv.email}? They will be shown as Active and can access the dashboard.`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/invites/${inv.id}/activate`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) load();
      else {
        const data = await res.json();
        alert(data.error || 'Failed to activate');
      }
    } catch (err) { console.error(err); }
  };

  const filtered = invites.filter(inv => {
    const matchArchived = inv.status !== 'archived' || filter === 'archived';
    const matchSearch = search === '' ||
      inv.email.toLowerCase().includes(search.toLowerCase()) ||
      (inv.introducer_name || '').toLowerCase().includes(search.toLowerCase());
    const status = getStatus(inv);
    const matchFilter = filter === 'all' || status === filter || 
      (filter === 'active' && status === 'agreement_approved') ||
      (filter === 'pending' && ['pending', 'accepted', 'awaiting_agreement', 'agreement_pending'].includes(status));
    return matchArchived && matchSearch && matchFilter;
  });

  const nonArchived = invites.filter(i => i.status !== 'archived');
  const counts = {
    all: nonArchived.length,
    active: nonArchived.filter(i => getStatus(i) === 'agreement_approved').length,
    pending: nonArchived.filter(i => ['pending', 'accepted', 'awaiting_agreement', 'agreement_pending'].includes(getStatus(i))).length,
    review: nonArchived.filter(i => getStatus(i) === 'agreement_pending').length,
  };

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: mobile ? 0 : '70px', paddingTop: mobile ? '64px' : '24px', padding: mobile ? '64px 16px 24px' : '24px', minHeight: '100vh', background: '#f9fafb' }}>
        <div style={{ maxWidth: 1100 }}>

          {/* Page header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
            <div>
              <h1 style={{ fontSize: 28, fontWeight: 700, color: '#0e1b2c', margin: '0 0 4px' }}>Introducers</h1>
              <p style={{ color: '#6b7280', margin: 0, fontSize: 14 }}>Manage your introducer network and agreements</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={load} style={styles.iconBtn}><RefreshCw size={15} /></button>
              <button onClick={() => setShowBulkModal(true)}
                title="Import your introducer list from a CSV file"
                style={{ ...styles.inviteBtn, background: '#fff', color: '#374151', border: '1px solid #d1d5db' }}>
                ⬆ Bulk Import
              </button>
              <button onClick={() => setShowModal(true)} style={styles.inviteBtn}>
                <Plus size={16} /> Invite Introducer
              </button>
            </div>
          </div>

          {/* Stat cards */}
          <div style={styles.statRow}>
            {[
              { label: 'Total', value: counts.all, color: '#6b7280' },
              { label: 'Active', value: counts.active, color: '#10b981' },
              { label: 'Pending', value: counts.pending, color: '#f59e0b' },
              { label: 'Awaiting Review', value: counts.review, color: '#3b82f6', highlight: counts.review > 0 },
            ].map(s => (
              <div key={s.label} style={{ ...styles.statCard, ...(s.highlight ? styles.statHighlight : {}) }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Filters + Search */}
          <div style={styles.toolbar}>
            <div style={styles.filterTabs}>
              {[['all', 'All'], ['active', 'Active'], ['pending', 'Pending'], ['agreement_pending', 'Needs Review'], ['expired', 'Expired'], ['archived', 'Archived']].map(([val, label]) => (
                <button key={val} onClick={() => setFilter(val)}
                  style={{ ...styles.filterTab, ...(filter === val ? styles.filterTabActive : {}) }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={styles.searchWrap}>
              <Search size={14} style={{ color: '#9ca3af', position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
              <input style={styles.searchInput} placeholder="Search by name or email..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>

          {/* Table */}
          <div style={styles.table}>
            <div style={styles.headerRow}>
              <span style={styles.c1}>Introducer</span>
              <span style={styles.c3}>Status</span>
              <span style={styles.c6}>Actions</span>
            </div>

            {loading ? (
              <div style={styles.empty}>Loading...</div>
            ) : filtered.length === 0 ? (
              <EmptyIntroducers onInvite={() => document.querySelector('[data-invite-btn]')?.click()} />
            ) : (
              filtered.map(inv => (
                <div key={inv.id} style={{ ...styles.row, cursor: 'pointer' }}
                  onClick={() => {
                    if (['pending','countersigned','with_compliance'].includes(inv.agreement_status) && inv.introducer_user_id) {
                      setReviewing({ id: inv.introducer_user_id, name: inv.introducer_name || inv.email });
                    } else if (inv.introducer_user_id) {
                      navigate(`/introducer/user/${inv.introducer_user_id}`);
                    } else {
                      setDetailsInv(inv);
                    }
                  }}>
                  <div style={styles.c1}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#0e1b2c' }}>{inv.introducer_name || inv.email}</div>
                    {inv.introducer_name && <div style={{ fontSize: 11, color: '#9ca3af' }}>{inv.email}</div>}
                    {inv.fee_value && <div style={{ fontSize: 11, color: '#4b5a6e', marginTop: 2 }}>{inv.fee_type === 'percentage' ? `${inv.fee_value}%` : `£${inv.fee_value}`}{inv.fee_applies_to_renewals ? ' +R' : ''}</div>}
                    {inv.id_upload_skipped && <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 2 }}>⚠️ ID pending</div>}
                  </div>
                  <div style={styles.c3}><StatusBadge inv={inv} /></div>
                  <div style={styles.c6}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {inv.status === 'pending' && (
                        <button onClick={e => { e.stopPropagation(); copyLink(inv.token); }} style={{ ...styles.copyBtn, background: copiedToken === inv.token ? '#10b981' : '', color: copiedToken === inv.token ? '#fff' : '', borderColor: copiedToken === inv.token ? '#10b981' : '' }}>{ copiedToken === inv.token ? '✓ Copied!' : 'Copy Link' }</button>
                      )}
                      {inv.status === 'expired' && (
                        <button onClick={e => { e.stopPropagation(); resendInvite(inv.email); }} style={styles.resendBtn}>Resend</button>
                      )}
                      {['pending','countersigned','with_compliance'].includes(inv.agreement_status) && inv.introducer_user_id && (
                        <button onClick={e => { e.stopPropagation(); setReviewing({ id: inv.introducer_user_id, name: inv.introducer_name || inv.email }); }}
                          style={styles.reviewBtn}>{inv.agreement_status === 'countersigned' ? 'Send to Network' : inv.agreement_status === 'with_compliance' ? 'Network Approved' : 'Review'}</button>
                      )}
                      {inv.introducer_user_id && (
                        getStatus(inv) === 'accepted' ||
                        (isPlatformOwner && getStatus(inv) === 'awaiting_agreement')
                      ) && (
                        <button onClick={e => { e.stopPropagation(); activateIntroducer(inv); }}
                          style={{ ...styles.reviewBtn, background: '#10b981' }}>Activate</button>
                      )}
                      {inv.introducer_user_id && inv.status !== 'archived' && inv.user_status !== 'suspended' &&
                        !['approved', 'pending', 'countersigned', 'with_compliance'].includes(inv.agreement_status) && (
                        <button onClick={e => { e.stopPropagation(); sendAgreementForSigning(inv); }}
                          disabled={sendingAgreementId === inv.introducer_user_id}
                          style={{ ...styles.reviewBtn, background: '#4f46e5', opacity: sendingAgreementId === inv.introducer_user_id ? 0.6 : 1 }}>
                          {sendingAgreementId === inv.introducer_user_id ? 'Sending...' : 'Send Agreement'}
                        </button>
                      )}
                      {inv.introducer_user_id && (
                        <button onClick={e => { e.stopPropagation(); navigate(`/introducer/user/${inv.introducer_user_id}`); }}
                          style={styles.viewBtn}>View Profile</button>
                      )}
                      {inv.introducer_user_id && inv.status !== 'archived' && (
                        <button onClick={e => { e.stopPropagation(); sendResetLink(inv); }}
                          style={styles.resendBtn}>🔑 Reset</button>
                      )}
                      {inv.status !== 'archived' && (
                        <button onClick={e => { e.stopPropagation(); archiveIntroducer(inv.id); }}
                          style={styles.archiveBtn}>Archive</button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showBulkModal && (
        <BulkInviteModal onClose={() => setShowBulkModal(false)} onSuccess={() => load()} />
      )}
      {showModal && (
        <InviteModal onClose={() => setShowModal(false)} onSuccess={() => { setShowModal(false); load(); }} />
      )}
      {detailsInv && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setDetailsInv(null)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, maxWidth: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: 18, color: '#0e1b2c' }}>{detailsInv.introducer_name || detailsInv.email}</h2>
              <button onClick={() => setDetailsInv(null)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#9ca3af', cursor: 'pointer', padding: 0 }}>×</button>
            </div>
            <p style={{ margin: '0 0 18px', fontSize: 13, color: '#9ca3af' }}>
              Invited {new Date(detailsInv.created_at).toLocaleDateString('en-GB')} — hasn't signed up yet.
              Their full profile appears here once they accept the invite.
            </p>
            {[
              ['Email', detailsInv.email],
              ['Fee arrangement', detailsInv.fee_value
                ? (detailsInv.fee_type === 'percentage' ? `${detailsInv.fee_value}% of commission` : `£${detailsInv.fee_value} fixed fee`) + (detailsInv.fee_applies_to_renewals ? ' (incl. renewals)' : '')
                : '—'],
              ['Handling adviser', detailsInv.adviser_name || '—'],
              ['FRN number', detailsInv.frn_number || '—'],
              ['Agreement required', detailsInv.agreement_required === false ? 'No — waived' : 'Yes'],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '9px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                <span style={{ color: '#9ca3af', fontWeight: 600 }}>{label}</span>
                <span style={{ color: '#0e1b2c', textAlign: 'right' }}>{value}</span>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              {detailsInv.status === 'pending' && (
                <button onClick={() => copyLink(detailsInv.token)} style={styles.copyBtn}>
                  {copiedToken === detailsInv.token ? '✓ Copied!' : 'Copy invite link'}
                </button>
              )}
              {detailsInv.status === 'expired' && (
                <button onClick={() => { resendInvite(detailsInv.email); setDetailsInv(null); }} style={styles.resendBtn}>Resend invite</button>
              )}
              <button onClick={() => setDetailsInv(null)} style={styles.viewBtn}>Close</button>
            </div>
          </div>
        </div>
      )}

      {reviewing && (
        <AgreementReviewPanel
          introducerId={reviewing.id}
          introducerName={reviewing.name}
          onClose={() => setReviewing(null)}
          onDecision={() => { setReviewing(null); load(); }}
        />
      )}
    </>
  );
}

const isMob = () => window.innerWidth < 640;

const styles = {
  inviteBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  iconBtn: { display: 'flex', alignItems: 'center', padding: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', color: '#6b7280' },
  statRow: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 20 },
  statCard: { background: '#fff', borderRadius: 10, padding: '14px 16px', border: '1px solid #e5e7eb' },
  statHighlight: { border: '1px solid #bfdbfe', background: '#eff6ff' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' },
  filterTabs: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  filterTab: { padding: '6px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' },
  filterTabActive: { background: '#10b981', color: '#fff', borderColor: '#10b981', fontWeight: 600 },
  searchWrap: { position: 'relative', flex: 1, minWidth: 160 },
  searchInput: { width: '100%', padding: '8px 10px 8px 30px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box' },
  table: { background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' },
  headerRow: { display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.4fr', padding: '10px 14px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' },
  row: { display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.4fr', padding: '12px 14px', borderBottom: '1px solid #f3f4f6', alignItems: 'center', gap: 8 },
  c1: {}, c2: {}, c3: {}, c4: { display: 'none' }, c5: { display: 'none' }, c6: { textAlign: 'right' },
  empty: { padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: 14 },
  copyBtn: { padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 11, color: '#374151', whiteSpace: 'nowrap' },
  reviewBtn: { padding: '5px 10px', borderRadius: 6, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  resendBtn: { padding: '5px 10px', borderRadius: 6, border: 'none', background: '#f59e0b', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  archiveBtn: { padding: '5px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#f9fafb', color: '#6b7280', cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  viewBtn: { padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
};
